import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, Check } from "lucide-react";

export default function AccountModal({ onClose }) {
  const { refreshUser } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (form.new_password !== form.confirm) { setError("New passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      await refreshUser();
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not change your password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="account-modal" className="relative z-10 w-full max-w-md rounded-sm border border-stone-200 bg-brand-linen p-8 animate-fade-up">
        <button data-testid="account-close" onClick={onClose} className="absolute right-5 top-5 text-stone-400 hover:text-stone-900 transition-colors"><X size={18} /></button>

        {done ? (
          <div data-testid="account-done">
            <div className="mb-3 flex items-center gap-2 text-brand-sage">
              <Check size={18} /> <span className="font-serif text-2xl text-stone-900">Password updated</span>
            </div>
            <p className="text-sm text-stone-600 mb-6">Your new password is saved. Use it next time you sign in.</p>
            <button onClick={onClose} className="w-full rounded-sm bg-brand-primary px-4 py-2.5 text-sm text-white hover:bg-brand-hover transition-colors">Done</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="label-eyebrow mb-2">Account</div>
            <h3 className="font-serif text-3xl text-stone-900 mb-6">Change password</h3>
            <div className="space-y-5">
              <div>
                <label className="text-sm text-stone-600">Current password</label>
                <input data-testid="account-current" type="password" required value={form.current_password} onChange={(e) => set("current_password", e.target.value)}
                  className="mt-1 w-full bg-transparent border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-brand-primary transition-colors" />
              </div>
              <div>
                <label className="text-sm text-stone-600">New password</label>
                <input data-testid="account-new" type="password" required value={form.new_password} onChange={(e) => set("new_password", e.target.value)}
                  className="mt-1 w-full bg-transparent border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-brand-primary transition-colors" placeholder="at least 6 characters" />
              </div>
              <div>
                <label className="text-sm text-stone-600">Confirm new password</label>
                <input data-testid="account-confirm" type="password" required value={form.confirm} onChange={(e) => set("confirm", e.target.value)}
                  className="mt-1 w-full bg-transparent border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-brand-primary transition-colors" />
              </div>
            </div>
            {error && <p data-testid="account-error" className="mt-4 text-sm text-[#9E4A41]">{error}</p>}
            <div className="mt-7 flex justify-end gap-3">
              <button type="button" onClick={onClose} className="rounded-sm border border-stone-300 px-4 py-2.5 text-sm text-stone-700 hover:border-stone-400 transition-colors">Cancel</button>
              <button data-testid="account-submit" type="submit" disabled={loading}
                className="rounded-sm bg-brand-primary px-5 py-2.5 text-sm text-white hover:bg-brand-hover transition-colors disabled:opacity-60">
                {loading ? "Saving…" : "Update password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
